import { CommonModule } from '@angular/common';
import { inject } from '@angular/core';
import { PAYMENT_REMINDER_OVERDUE_SECONDS, PaymentReminderReaction, PaymentReminderTarget, PaymentReminderType, StorePaymentReminderInteractionRequest } from '@common/payment-reminder/shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiIconsModule } from '@common/ui/frontend-shared/icons';
import { UiInputGroupComponent } from '@common/ui/frontend-shared/input-group';
import { AccessFacade, AccessState, AppRequestService, ConfigService, LocalMessageHandlerService } from '@core/frontend-shared';
import { htmlEscape } from '@core/shared/utils';
import { dayjs } from '@core/shared/utils/dayjs';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
import * as i2 from "@angular/common";
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "date");
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const state_r1 = i0.ɵɵnextContext(2);
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate1(" Es wurde am ", i0.ɵɵpipeBind1(2, 1, ctx_r1.getPaymentConfirmedDate(state_r1.reminder)), " angegeben, dass die Zahlung ausgef\u00FChrt wurde. ");
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 9);
    i0.ɵɵlistener("click", function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Conditional_2_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r4);
      const ctx_r1 = i0.ɵɵnextContext(5);
      return i0.ɵɵresetView(ctx_r1.onClickReaction(ctx_r1.PaymentReminderReaction.postpone));
    });
    i0.ɵɵelementEnd();
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r3 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "ui-input-group", 6)(1, "button", 7);
    i0.ɵɵlistener("click", function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Template_button_click_1_listener() {
      i0.ɵɵrestoreView(_r3);
      const ctx_r1 = i0.ɵɵnextContext(4);
      return i0.ɵɵresetView(ctx_r1.onClickReaction(ctx_r1.PaymentReminderReaction.confirm));
    });
    i0.ɵɵelementEnd();
    i0.ɵɵconditionalCreate(2, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Conditional_2_Template, 1, 0, "button", 8);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const state_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("rounded", true);
    i0.ɵɵadvance();
    i0.ɵɵproperty("label", state_r1.paymentConfirmed ? "Zahlung erneut best\u00E4tigen" : "Zahlung best\u00E4tigen");
    i0.ɵɵadvance();
    i0.ɵɵconditional(state_r1.reminder.type !== "escalate" ? 2 : -1);
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵtext(1, " Vielen Dank f\u00FCr Ihre Antwort. Unsere Mitarbeiter wurden informiert und pr\u00FCfen den Zahlungseingang. ");
    i0.ɵɵelementEnd();
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_0_Template, 3, 3, "p");
    i0.ɵɵconditionalCreate(1, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_1_Template, 3, 3, "ui-input-group", 6);
    i0.ɵɵconditionalCreate(2, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Conditional_2_Template, 2, 0, "p");
  }
  if (rf & 2) {
    const state_r1 = i0.ɵɵnextContext();
    i0.ɵɵconditional(state_r1.paymentConfirmed ? 0 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!state_r1.interactedInCurrentSession ? 1 : -1);
    i0.ɵɵadvance();
    i0.ɵɵconditional(state_r1.interactedInCurrentSession ? 2 : -1);
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "div", 3, 0)(3, "div", 4)(4, "h2");
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelement(6, "p", 5);
    i0.ɵɵconditionalCreate(7, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Conditional_7_Template, 3, 3);
    i0.ɵɵelementEnd()()();
  }
  if (rf & 2) {
    const state_r1 = ctx;
    i0.ɵɵclassMap("position-overlay");
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate(state_r1.title);
    i0.ɵɵadvance();
    i0.ɵɵproperty("innerHTML", state_r1.message, i0.ɵɵsanitizeHtml);
    i0.ɵɵadvance();
    i0.ɵɵconditional(state_r1.isResponsibleUser ? 7 : -1);
  }
}
function PaymentReminderOverlayComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵconditionalCreate(0, PaymentReminderOverlayComponent_Conditional_0_Conditional_0_Template, 8, 5, "div", 1);
    i0.ɵɵpipe(1, "async");
  }
  if (rf & 2) {
    let tmp_1_0;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵconditional((tmp_1_0 = i0.ɵɵpipeBind1(1, 1, ctx_r1.state$)) ? 0 : -1, tmp_1_0);
  }
}
export let PaymentReminderOverlayComponent = /*#__PURE__*/(() => {
  class PaymentReminderOverlayComponent {
    constructor(access, messages, config, api) {
      this.access = access;
      this.messages = messages;
      this.config = config;
      this.api = api;
      this.managedCompany$ = inject(Store).select(AccessState.accessData('managedCompany'));
      this.paymentReminder$ = inject(Store).select(AccessState.accessData('paymentReminder'));
      this.oldInteractions$ = inject(Store).select(AccessState.accessData('userId')).pipe(withLatestFrom(this.paymentReminder$), map(([userId, reminder]) => {
        if (!userId || !reminder) return [];
        const personalInteractions = Array.isArray(reminder.interactions) ? reminder.interactions.filter(i => i.userId === userId) : [];
        return personalInteractions;
      }));
      this.newInteraction$ = new BehaviorSubject(null);
      this.allInteractions$ = combineLatest([this.oldInteractions$, this.newInteraction$]).pipe(map(([oldInteractions, newInteraction]) => {
        let interactions = [];
        if (newInteraction) {
          interactions = [...(oldInteractions || []), newInteraction];
        } else {
          interactions = [...oldInteractions];
        }
        interactions.sort((a, b) => {
          return dayjs(b.creationDate).isBefore(a.creationDate) ? 1 : -1;
        });
        return interactions;
      }));
      this.display$ = this.paymentReminder$.pipe(switchMap(reminder => {
        if (!reminder) return of(false);
        return combineLatest({
          interactions: this.allInteractions$,
          interactedInCurrentSession: this.newInteraction$.pipe(map(i => !!i)),
          managedCompany: this.managedCompany$
        }).pipe(map(({
          interactions,
          managedCompany,
          interactedInCurrentSession
        }) => {
          const display = this.shouldDisplay(reminder, managedCompany, interactions, interactedInCurrentSession);
          // console.log('display?',display, {reminder, interactions, interactedInCurrentSession, managedCompany})
          return display;
        }));
      }));
      this.state$ = combineLatest({
        reminder: this.paymentReminder$,
        managedCompany: this.managedCompany$,
        interactedInCurrentSession: this.newInteraction$.pipe(map(i => !!i))
      }).pipe(map(({
        reminder,
        managedCompany,
        interactedInCurrentSession
      }) => {
        const {
          title,
          message
        } = this.calculateTitleAndMessage(reminder, managedCompany);
        const isResponsibleUser = reminder.target === PaymentReminderTarget.user || reminder.target === PaymentReminderTarget.company && !!managedCompany;
        const paymentConfirmed = reminder.interactions?.some(i => i.reaction === PaymentReminderReaction.confirm);
        // console.log('state.interactedInCurrentSession',interactedInCurrentSession)
        return {
          reminder,
          title,
          message,
          interactedInCurrentSession,
          isResponsibleUser,
          paymentConfirmed
        };
      }));
      this.PaymentReminderReaction = PaymentReminderReaction;
      this.PaymentReminderType = PaymentReminderType;
    }
    shouldDisplay(reminder, managedCompany, pastPersonalInteractions, interactedInCurrentSession) {
      if (!reminder) return false;
      if (reminder.type === PaymentReminderType.escalate) {
        // console.log("escalate, block all accounts");
        return true; // escalate shows a non-dismissable overlay for ALL users
      }
      if (reminder.target === PaymentReminderTarget.company && !managedCompany) {
        // console.log("hidden cause this aint a manager account");
        return false; // show only to manager accounts
      }
      // we now know that the overlay should be shown, IF it is not postponed right now.
      // console.log('display calc - we are a manager + reminder is not escalating')
      // has interacted with reminder in THIS session
      if (interactedInCurrentSession) {
        // console.log("interactedInCurrentSession");
        return false;
      }
      if (pastPersonalInteractions.length) {
        // has interacted with reminder in PREVIOUS session
        if (reminder.type === PaymentReminderType.warn) {
          // if user has confirmed the payment, do not show any more warnings
          if (pastPersonalInteractions.some(i => i.reaction === PaymentReminderReaction.confirm)) {
            return false;
          } else {
            return true; // in warning mode, we show the overlay on each visit
          }
        } else {
          const overdueDate = dayjs().subtract(PAYMENT_REMINDER_OVERDUE_SECONDS, 'second');
          const isOverdue = overdueDate.isAfter(pastPersonalInteractions[0].creationDate);
          // console.log('isOverdue?', overdueDate, isOverdue)
          return isOverdue; // in reminder mode, we show the overlay only once per user
        }
      } else {
        return true;
      }
    }
    calculateTitleAndMessage(reminder, managedCompany) {
      let message;
      let title;
      if (!reminder) return {
        title,
        message
      };
      if (reminder.type === PaymentReminderType.escalate) {
        title = 'Account gesperrt';
        if (reminder.target === PaymentReminderTarget.company) {
          if (managedCompany) {
            message = 'Die Accounts Ihres Unternehmens sind aktuell gesperrt, da mehrere Zahlungen ausstehen. ' + 'Bitte bestätigen Sie die Ausführung der offenen Zahlungen. \n \n' + 'Sobald der offene Betrag eingegangen ist, schalten unsere Mitarbeiter den Zugang wieder frei.';
          } else {
            message = 'Die Accounts Ihres Unternehmens sind aktuell gesperrt. ' + 'Bitte wenden Sie sich für weitere Informationen an einen Manager / eine Managerin bzw. Ihre Pflegedienstleitung.';
          }
        } else {
          message = 'Ihr Account wurde gesperrt, da mehrere Zahlungen ausstehen. ' + 'Bitte tätigen Sie die Zahlung und bestätigen Sie diese. \n \n' + 'Unsere Mitarbeiter werden dann informiert und schalten Ihren Account wieder frei, sobald der offene Betrag beglichen wurde.';
        }
      } else {
        title = PaymentReminderType.warn ? 'Wichtiger Hinweis' : 'Erinnerung';
        message = 'Leider konnten wir keinen Eingang Ihres Mitgliedsbeitrags verzeichnen. Bitte prüfen Sie, ob die Zahlung korrekt veranlasst wurde.';
      }
      // overwrite default message with custom one
      if (reminder.message) {
        if (reminder.target === PaymentReminderTarget.company && managedCompany) {
          message = reminder.message;
        } else if (reminder.target === PaymentReminderTarget.user) {
          message = reminder.message;
        }
      }
      message = htmlEscape(message).replace(/\n/gm, "<br/>");
      return {
        title,
        message
      };
    }
    onClickReaction(reaction) {
      this.paymentReminder$.pipe(take(1)).subscribe(reminder => {
        this.api.sendRequest(StorePaymentReminderInteractionRequest, {
          reminderId: reminder.id,
          reaction
        }).subscribe(response => {
          this.newInteraction$.next(response.interaction);
        });
      });
    }
    getPaymentConfirmedDate(reminder) {
      const paymentConfirmed = reminder.interactions?.find(i => i.reaction === PaymentReminderReaction.confirm);
      return paymentConfirmed?.creationDate || null;
    }
    static {
      this.ɵfac = function PaymentReminderOverlayComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PaymentReminderOverlayComponent)(i0.ɵɵdirectiveInject(i1.AccessFacade), i0.ɵɵdirectiveInject(i1.LocalMessageHandlerService), i0.ɵɵdirectiveInject(i1.ConfigService), i0.ɵɵdirectiveInject(i1.AppRequestService));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: PaymentReminderOverlayComponent,
        selectors: [["payment-reminder-overlay"]],
        features: [i0.ɵɵProvidersFeature([LocalMessageHandlerService])],
        decls: 2,
        vars: 3,
        consts: [["dialog", ""], [1, "wrapper", 3, "class"], [1, "wrapper"], [1, "dialog"], [1, "content"], [3, "innerHTML"], [3, "rounded"], ["uiButton", "", "icon", "check", 1, "ui-button-primary", 3, "click", "label"], ["uiButton", "", "icon", "volume-xmark", "label", "Sp\u00E4ter erneut erinnern", 1, "ui-button-grey"], ["uiButton", "", "icon", "volume-xmark", "label", "Sp\u00E4ter erneut erinnern", 1, "ui-button-grey", 3, "click"]],
        template: function PaymentReminderOverlayComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, PaymentReminderOverlayComponent_Conditional_0_Template, 2, 3);
            i0.ɵɵpipe(1, "async");
          }
          if (rf & 2) {
            i0.ɵɵconditional(i0.ɵɵpipeBind1(1, 1, ctx.display$) ? 0 : -1);
          }
        },
        dependencies: [CommonModule, UiIconsModule, UiButtonComponent, UiInputGroupComponent, i2.AsyncPipe, i2.DatePipe],
        styles: [".wrapper[_ngcontent-%COMP%]{position:fixed;z-index:1000}.dialog[_ngcontent-%COMP%]{background:var(--contentColor);color:var(--contentTextColor);padding:8px 12px 12px;border-radius:4px}.dialog[_ngcontent-%COMP%]   .content[_ngcontent-%COMP%]{padding:0 0 10px}.wrapper.position-overlay[_ngcontent-%COMP%]{background:var(--invertedOverlaySoftColor);-webkit-backdrop-filter:blur(4px);backdrop-filter:blur(4px);inset:0}.wrapper.position-overlay[_ngcontent-%COMP%]{display:flex;align-items:center;justify-content:center}.wrapper.position-overlay[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%]{position:static}@media (min-width: 768px){.dialog[_ngcontent-%COMP%]{padding:20px 20px 22px}.position-overlay[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%], .position-modal[_ngcontent-%COMP%]   .dialog[_ngcontent-%COMP%]{bottom:var(--trackingConsentPositionBottom, 20px);left:initial;left:20px;width:600px;min-width:400px;max-width:40%}}"],
        changeDetection: 0
      });
    }
  }
  return PaymentReminderOverlayComponent;
})();